import { ROUTES } from "@app/constants";
import { BulkAddReportMedia } from "@features/BulkAddReportMedia";
import { Button, Modal } from "@shared/ui";
import { DisabledPopover } from "@shared/ui/DisabledPopover";
import { PageHeader } from "@widgets/PageHeader";
import { Download, FilePlus, Settings } from "lucide-react";
import { useState } from "react";
import styles from "./ReportHeader.module.scss";

type ReportHeaderProps = {
  reportName: string;
  reportType?: "Custom" | "Brief";
  reportId: string;
  onAddContent?: () => void;
  isInBrief: boolean;
  onExportReport?: () => void;
  isExporting?: boolean;
  isAddContentButtonDisabled?: boolean;
  onCustomizeFields?: () => void;
};

export function ReportHeader({
  reportName,
  reportType,
  reportId,
  onAddContent,
  isInBrief,
  onExportReport,
  isExporting = false,
  isAddContentButtonDisabled = false,
  onCustomizeFields,
}: ReportHeaderProps) {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  return (
    <div className={styles.header}>
      {!isInBrief && (
        <PageHeader
          title={
            <div className={styles.titleSection}>
              <h1 className={styles.breadcrumbTitle}>{reportName}</h1>
              {reportType && (
                <span
                  className={`${styles.typeBadge} ${
                    reportType === "Custom"
                      ? styles.typeBadgeCustom
                      : styles.typeBadgeStandard
                  }`}
                >
                  {reportType === "Custom" ? "Custom" : "Campaign"}
                </span>
              )}
            </div>
          }
          backText="Reports"
          backTo={ROUTES.REPORTS}
        />
      )}

      <div className={styles.headerActions}>
        {reportType === "Custom" && onAddContent && (
          <DisabledPopover isDisabled={isAddContentButtonDisabled}>
            <Button
              type="primary"
              className={styles.addContentButton}
              onClick={onAddContent}
              disabled={isAddContentButtonDisabled}
            >
              <FilePlus size={14} /> Add Content
            </Button>
          </DisabledPopover>
        )}

        <Button
          className={styles.exportButton}
          onClick={onExportReport}
          loading={isExporting}
        >
          <Download size={14} /> Export
        </Button>

        {onCustomizeFields && (
          <Button onClick={onCustomizeFields}>
            <Settings size={14} /> Customize Fields
          </Button>
        )}

        {reportType === "Custom" && (
          <DisabledPopover isDisabled={isAddContentButtonDisabled}>
            <Button
              onClick={() => setIsAddModalOpen(true)}
              disabled={isAddContentButtonDisabled}
            >
              <FilePlus size={14} /> Add Media Links
            </Button>
          </DisabledPopover>
        )}
      </div>

      <Modal
        open={isAddModalOpen}
        onCancel={() => setIsAddModalOpen(false)}
        title="Add Media Links to Report"
        footer={null}
        styles={{
          body: { padding: 0 },
          content: { padding: 0 },
          header: { paddingLeft: 20, paddingRight: 20, paddingTop: 20 },
        }}
        width={800}
      >
        <BulkAddReportMedia
          reportId={reportId}
          onClose={() => setIsAddModalOpen(false)}
        />
      </Modal>
    </div>
  );
}
